import { useRef } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import * as SelectPrimitive from '@radix-ui/react-select'

import { cn } from '@/lib/utils'

type SelectValue = ValueOption['value']

type SelectProps = {
  options: ValueOption[]
  value?: SelectValue
  onValueChange: (value: SelectValue) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export const Select = ({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
}: SelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const valueMap = new Map<string, SelectValue>()
  const normalizedOptions = options
    .filter(option => option.value !== null)
    .map(option => {
      const normalizedValue = String(option.value)
      valueMap.set(normalizedValue, option.value)
      return { ...option, normalizedValue }
    })

  const normalizedValue =
    value === null || value === undefined ? undefined : String(value)

  return (
    <SelectPrimitive.Root
      value={normalizedValue}
      onValueChange={next => {
        onValueChange(valueMap.get(next) ?? null)
        requestAnimationFrame(() => triggerRef.current?.focus())
      }}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        ref={triggerRef}
        className={cn(
          'px-3 ml-1 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 inline-flex items-center justify-between gap-2',
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown className='h-4 w-4 text-gray-500' />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position='popper'
          side='bottom'
          sideOffset={6}
          avoidCollisions={false}
          className='z-50 overflow-hidden rounded-md border bg-white shadow-md'
        >
          <SelectPrimitive.Viewport className='p-1 max-h-[40vh] overflow-y-auto'>
            {normalizedOptions.map(option => (
              <SelectPrimitive.Item
                key={option.normalizedValue}
                value={option.normalizedValue}
                disabled={option.isDisabled}
                className={cn(
                  'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                  'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
                  <SelectPrimitive.ItemIndicator>
                    <Check className='h-4 w-4' />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>
                  {option.label}
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
